/**
 * @typedef {import('@/types/structured-data-editor').ApiExtractionRow} ApiExtractionRow
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 * @typedef {import('@/types/structured-data-editor').SyncPreviewRow} SyncPreviewRow
 */

/**
 * @param {SdeRow} row
 * @returns {boolean}
 */
export function hasExtractedProductName(row) {
    const name = row?.fields?.name?.value;
    if (name == null) {
        return false;
    }
    return String(name).trim() !== "";
}

/**
 * @param {SdeRow} row
 * @returns {boolean}
 */
export function shouldKeepSdeRow(row) {
    if (row?.isManualRow) {
        return true;
    }
    if (row?.extractionSeq != null) {
        return hasExtractedProductName(row);
    }
    return true;
}

/**
 * @param {ApiExtractionRow} apiRow
 * @param {number} rowIndex
 * @returns {SdeRow|null}
 */
export function apiRowToSdeRow(apiRow, rowIndex) {
    /** @type {SdeRow['fields']} */
    const fields = {};
    Object.entries(apiRow.fields || {}).forEach(([key, cell]) => {
        fields[key] = {
            key,
            value: cell.value,
            confidence: {
                score: cell.confidence?.score ?? 50,
                level: cell.confidence?.level ?? "medium",
                rawValue: cell.confidence?.raw_value ?? null,
                suggestion: cell.confidence?.suggestion ?? null,
                aiValue: cell.confidence?.ai_value ?? null,
            },
        };
    });
    const row = {
        rowIndex,
        extractionSeq: apiRow.seq,
        fields,
        excluded: false,
        syncStatus: "pending",
    };
    return shouldKeepSdeRow(row) ? row : null;
}

/**
 * @param {SyncPreviewRow} previewRow
 * @param {number} rowIndex
 * @returns {SdeRow}
 */
export function previewRowToSdeRow(previewRow, rowIndex) {
    const row = apiRowToSdeRow(
        {
            fields: previewRow.fields,
        },
        rowIndex
    );
    if (!row) {
        return null;
    }
    const rawId = previewRow.sync_payload?.id;
    if (rawId != null && rawId !== "") {
        const instanceId = Number(rawId);
        if (!Number.isNaN(instanceId)) {
            row.instanceId = instanceId;
        }
    }
    return row;
}

/**
 * @param {ApiExtractionRow[]} apiRows
 * @param {number} [startIndex]
 * @returns {SdeRow[]}
 */
export function apiRowsToSdeRows(apiRows, startIndex = 0) {
    /** @type {SdeRow[]} */
    const rows = [];
    apiRows.forEach((apiRow) => {
        const row = apiRowToSdeRow(apiRow, startIndex + rows.length);
        if (row) {
            rows.push(row);
        }
    });
    return rows;
}
